import { useState } from "react";
import { Crosshair, Loader2 } from "lucide-react";
import { location } from "../platform";
import { GeoApi } from "../api/services";
import { MapView } from "../maps/MapView";

export type Place = { lat: number; lng: number; address: string };

type Props = {
  label: string;
  value: Place | null;
  onChange: (p: Place) => void;
  allowGps?: boolean;
  kind?: "pickup" | "destination";
  testId: string;
};

export function LocationPicker({ label, value, onChange, allowGps = true, kind = "pickup", testId }: Props) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const useGps = async () => {
    setBusy(true); setErr("");
    try {
      const p = await location.getCurrentPosition();
      onChange({ lat: p.lat, lng: p.lng, address: value?.address || "" });
      const r = await GeoApi.reverse(p.lat, p.lng).catch(() => null);
      if (r?.address) onChange({ lat: p.lat, lng: p.lng, address: r.address });
    } catch (e: any) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3" data-testid={testId}>
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-semibold text-slate-800">{label}</span>
        {allowGps && (
          <button type="button" onClick={useGps} disabled={busy} data-testid={`${testId}-gps-button`} className="fp-btn-secondary min-h-[40px] px-3 text-xs">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Crosshair size={14} />} Use my current location
          </button>
        )}
      </div>
      <p className="text-xs text-slate-500">
        {allowGps ? "Location is used only when you tap the button above (your browser will ask permission). " : ""}Or tap the map to drop a pin.
      </p>
      {err && <p className="rounded-lg bg-amber-50 p-2 text-xs text-amber-900" data-testid={`${testId}-error`}>{err}</p>}
      <MapView className="h-56 sm:h-64" testId={`${testId}-map`} onPick={(lat, lng) => onChange({ lat, lng, address: value?.address || "" })}
        markers={value ? [{ id: "p", lat: value.lat, lng: value.lng, kind }] : []} />
      {value && <p className="font-mono text-xs text-slate-500" data-testid={`${testId}-coords`}>{value.lat.toFixed(5)}, {value.lng.toFixed(5)}</p>}
      <input className="fp-input" placeholder="House/Unit, Street, Barangay, City (e.g. 123 Osmeña Blvd, Kalubihan, Cebu City)"
        value={value?.address || ""} disabled={!value} data-testid={`${testId}-address-input`}
        onChange={(e) => value && onChange({ ...value, address: e.target.value })} />
    </div>
  );
}
